import { PriceTrackers } from '@vae/server/dist/client'
import { ChartOptions, Tick } from 'chart.js'
import { _DeepPartialObject } from 'chart.js/dist/types/utils'
import {
	BoxAnnotationOptions,
	LineAnnotationOptions,
	PartialEventContext,
} from 'chartjs-plugin-annotation'
import { getColor } from '../../../../../modals/trackerModal/utils/getColor'
import { getFontSize } from '../../../../../utils/getFontSize'
import { roundTicksTo1Decimal } from '../../../utils/roundTicksTo1Decimal'
import { Annotation } from '../MarkedPeriodsDetails'
import { priceString } from '../../../../../utils/priceUtils'

interface PriceChartOptionsProps {
	activeElement: number
	displayedTrackers: PriceTrackers[]
	xLabels: string[]
	isDarkMode: boolean
	onHover?: (index: number) => void
	annotations: Annotation[]
}

export const getPriceChartOptions = ({
	activeElement,
	displayedTrackers,
	xLabels,
	isDarkMode,
	onHover,
	annotations,
}: PriceChartOptionsProps): ChartOptions<'line' | 'bar'> => {
	const leftTickColor = isDarkMode ? '#3cb49f' : '#19b338'
	const gridColor = isDarkMode ? '#144448' : '#f5f5f5'
	const xTickColor = isDarkMode ? '#e3e3e3' : '#858585'
	const tooltipColor = isDarkMode ? '#3cb49f' : '#127d27'
	const tooltipBackgroundColor = isDarkMode ? '#0d2d30' : '#ffffff'
	const tooltipTextColor = isDarkMode ? '#e3e3e3' : '#858585'

	const fontSize = getFontSize()

	return {
		elements: {
			point: {
				borderColor: 'white',
				hitRadius: 5,
			},
			line: {
				tension: 0.5,
			},
		},
		layout: {
			autoPadding: true,
			padding: {
				left: 14,
				right: 14,
			},
		},
		devicePixelRatio: 2,
		maintainAspectRatio: false,
		animation: {
			duration: 800,
		},
		transitions: {
			active: {
				animation: {
					duration: 0
				}
			}
		},
		scales: {
			x: {
				stacked: true,
				ticks: {
					maxTicksLimit: 12,
					minRotation: 45,
					maxRotation: 45,
					color: xTickColor,
					font: {
						size: fontSize,
					},
				},
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
			},
			y: {
				stacked: true,
				grace: '10%',
				ticks: {
					color: leftTickColor,
					autoSkip: true,
					maxTicksLimit: 8,
					font: {
						size: fontSize,
					},
					callback: (tickValue: string | number, index: number, ticks: Tick[]) => {
						if (index !== 0) {
							return roundTicksTo1Decimal(tickValue, index, ticks)
						}
					},
				},
				grid: {
					color: gridColor,
					drawTicks: false,
				},
				border: {
					display: false,
				},
			},
		},
		plugins: {
			tooltip: {
				enabled: true,
				titleFont: {
					size: fontSize + 2,
					weight: 'bold',
				},
				bodyFont: {
					size: fontSize,
				},
				callbacks: {
					label: (item) => `${priceString(item.raw as number)} snt/kWh`,
					afterTitle: (tooltipItems) => {
						if (tooltipItems && tooltipItems.length > 0 && onHover) {
							onHover(tooltipItems[0].dataIndex)
						}
					},
				},
			},
			legend: { display: false },
			annotation: {
				clip: false,
				annotations: [
					// Vertical background boxes
					...annotations.map(
						i =>
							({
								type: i.type,
								xMin: i.xMin, // shift left to include full bar area
								xMax: i.xMax, // shift right to include full bar area
								backgroundColor: i.backgroundColor,
								borderWidth: i.borderWidth,
								drawTime: i.drawTime,
							}) as _DeepPartialObject<BoxAnnotationOptions>
					),
					// Active vertical line
					{
						type: 'line',
						xMin: activeElement,
						xMax: activeElement,
						borderColor: tooltipColor,
						borderWidth: 2,
						drawTime: 'beforeDatasetsDraw',
						display: true,
					},
					// Tracker value lines
					...displayedTrackers.map(
						tracker =>
							({
								type: 'line' as const,
								yMin: tracker.value,
								yMax: tracker.value,
								borderColor: getColor(tracker.limitType),
								drawTime: 'afterDatasetsDraw',
								display: context => {
									const scale = context.chart.scales.y
									return tracker.value <= scale.max && tracker.value >= scale.min
								},
							}) as _DeepPartialObject<LineAnnotationOptions>
					),
					// Label for active element
					{
						type: 'label',
						xValue: activeElement,
						yValue: (context: PartialEventContext) => context.chart.scales.y.max * 0.9,
						borderRadius: 6,
						borderWidth: 2,
						content: xLabels[activeElement],
						borderColor: tooltipColor,
						color: tooltipTextColor,
						backgroundColor: tooltipBackgroundColor,
						drawTime: 'beforeDatasetsDraw',
						display: true,
						font: {
							size: fontSize,
						},
					},
				],
			},
		},
	}
}
